import React, { useState, useMemo } from 'react';
import { X, Calendar, Check, Clock, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { useToast } from '../hooks/use-toast';
import { api, friendlyError } from '../lib/api';
import { track, EventTypes } from '../lib/analytics';
import { FormError, FormBanner } from './FormError';
import useFormValidation from '../hooks/useFormValidation';

// Format a YYYY-MM-DD string into a friendly Colombian Spanish full date.
// Uses local date parts to avoid the UTC drift bug (a "2026-02-13" treated as
// midnight UTC shifts to Feb 12 in Colombia / America/Bogota at GMT-5).
function formatFriendlyDate(yyyyMmDd) {
  if (!yyyyMmDd) return '';
  const [y, m, d] = yyyyMmDd.split('-').map(Number);
  const local = new Date(y, m - 1, d);
  return local.toLocaleDateString('es-CO', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });
}

// Format HH:MM (24h) -> "9:30 AM" / "3:00 PM"
function formatFriendlyTime(hhmm) {
  if (!hhmm) return '';
  const [h, m] = hhmm.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const display = ((h + 11) % 12) + 1;
  return `${display}:${String(m).padStart(2, '0')} ${period}`;
}

// Today as YYYY-MM-DD in America/Bogota (Colombia, GMT-5, no DST)
function todayInBogota() {
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Bogota', year: 'numeric', month: '2-digit', day: '2-digit',
  });
  return fmt.format(new Date()); // e.g. "2026-02-13"
}

// Suggested quick chips — useful for mobile, but the user can still pick anything
const QUICK_TIMES = [
  { label: 'Mañana', value: '10:00' },
  { label: 'Mediodía', value: '12:00' },
  { label: 'Tarde', value: '15:00' },
  { label: 'Tarde-noche', value: '17:00' },
];

export default function InspectionModal({ property, onClose }) {
  const { toast } = useToast();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [date, setDate] = useState('');   // YYYY-MM-DD
  const [time, setTime] = useState('');   // HH:MM (24h)
  const [form, setForm] = useState({ name: '', email: '', phone: '', notes: '' });
  const v = useFormValidation();

  const setField = (k, val) => { setForm((f) => ({ ...f, [k]: val })); v.clearField(k); };

  const minDate = useMemo(() => todayInBogota(), []);

  // Build the quick-date chips: hoy / mañana / pasado / +1 semana — local to Bogota
  const quickDates = useMemo(() => {
    const [y, m, d] = minDate.split('-').map(Number);
    const base = new Date(y, m - 1, d);
    const mk = (offset, label) => {
      const dt = new Date(base);
      dt.setDate(dt.getDate() + offset);
      const iso = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
      return { label, iso, weekday: dt.toLocaleDateString('es-CO', { weekday: 'short' }), day: dt.getDate() };
    };
    return [
      mk(0, 'Hoy'),
      mk(1, 'Mañana'),
      mk(2, 'Pasado'),
      mk(7, 'En 1 semana'),
    ];
  }, [minDate]);

  // Combined client-side guard: can't pick a past date (HTML min= helps, but iOS Safari ignores it sometimes)
  const dateIsValid = date && date >= minDate;
  const timeIsValid = /^\d{2}:\d{2}$/.test(time);

  // If user picks TODAY, also enforce the time isn't in the past (Bogota now)
  const timeNotInPast = useMemo(() => {
    if (!dateIsValid || !timeIsValid) return true;
    if (date !== minDate) return true;
    const nowFmt = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'America/Bogota', hour: '2-digit', minute: '2-digit', hour12: false,
    }).format(new Date()); // e.g. "14:35"
    return time > nowFmt;
  }, [date, time, minDate, dateIsValid, timeIsValid]);

  const canContinue = dateIsValid && timeIsValid && timeNotInPast;

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    v.reset();
    const ok = v.validate({
      name: !form.name || form.name.trim().length < 2 ? 'El nombre debe tener al menos 2 caracteres.' : null,
      email: !form.email ? 'Email es obligatorio.'
        : !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email) ? 'Email no tiene un formato válido.'
        : null,
      phone: !form.phone || form.phone.trim().length < 7 ? 'Ingresa un teléfono válido.' : null,
    });
    if (!ok) return;
    setSubmitting(true);
    try {
      await api.scheduleInspection({
        property_id: property.id,
        name: form.name,
        email: form.email,
        phone: form.phone,
        date,
        time: formatFriendlyTime(time),
        notes: form.notes,
      });
      // Lead conversion event — mapped to Meta Pixel `Lead`, GA4 `lead_created`,
      // Clarity custom event. Non-dot event → skips backend /events/track.
      try {
        track(EventTypes.LEAD_CREATED, {
          property_id: property.id,
          lead_type: 'visit_request',
          agent_id: property.owner_id,
        });
      } catch (err) { void err; }
      setStep(3);
    } catch (err) {
      const first = v.applyError(err, 'Intenta nuevamente o elige otro horario.');
      if (!first) {
        toast({
          title: 'No pudimos agendar la visita',
          description: friendlyError(err, 'Intenta nuevamente o elige otro horario.'),
          variant: 'destructive',
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 animate-in fade-in"
      data-testid="inspection-modal"
    >
      <div className="bg-white rounded-t-3xl sm:rounded-3xl max-w-2xl w-full max-h-[92vh] overflow-y-auto shadow-2xl">
        <div className="p-5 sm:p-6 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white z-10">
          <div className="min-w-0">
            <h2 className="font-serif text-2xl font-bold text-habita-dark">Agendar visita</h2>
            <p className="text-sm text-gray-500 mt-1 line-clamp-1">{property.title}</p>
          </div>
          <button
            onClick={onClose}
            data-testid="inspection-close"
            className="w-10 h-10 shrink-0 rounded-full hover:bg-gray-100 flex items-center justify-center"
            aria-label="Cerrar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {step === 1 && (
          <div className="p-5 sm:p-6 space-y-7">
            {/* DATE */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Calendar className="w-5 h-5 text-habita" />
                <h3 className="font-semibold">Elige una fecha</h3>
              </div>
              <div className="grid grid-cols-4 gap-2 mb-3">
                {quickDates.map(q => {
                  const active = date === q.iso;
                  return (
                    <button
                      key={q.iso}
                      type="button"
                      onClick={() => setDate(q.iso)}
                      data-testid={`inspection-quickdate-${q.label.toLowerCase().replace(/\s/g, '-')}`}
                      className={`p-2.5 rounded-xl border text-center transition-all ${
                        active
                          ? 'bg-habita text-white border-habita'
                          : 'bg-white border-gray-200 hover:border-habita text-gray-700'
                      }`}
                    >
                      <p className={`text-[10px] uppercase font-semibold ${active ? 'text-white/85' : 'text-gray-500'}`}>{q.label}</p>
                      <p className="text-base font-bold mt-0.5 leading-none">{q.day}</p>
                      <p className={`text-[10px] mt-0.5 ${active ? 'text-white/80' : 'text-gray-400'}`}>{q.weekday}</p>
                    </button>
                  );
                })}
              </div>
              <Label className="text-xs uppercase tracking-widest text-gray-500">O elige cualquier fecha</Label>
              <Input
                type="date"
                value={date}
                min={minDate}
                onChange={(e) => setDate(e.target.value)}
                data-testid="inspection-date-input"
                className="mt-1.5 h-12 text-base"
              />
              {date && !dateIsValid && (
                <p className="text-xs text-rose-600 mt-1.5">Selecciona una fecha futura.</p>
              )}
            </div>

            {/* TIME */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Clock className="w-5 h-5 text-habita" />
                <h3 className="font-semibold">Elige una hora</h3>
              </div>
              <div className="grid grid-cols-4 gap-2 mb-3">
                {QUICK_TIMES.map(q => {
                  const active = time === q.value;
                  return (
                    <button
                      key={q.value}
                      type="button"
                      onClick={() => setTime(q.value)}
                      data-testid={`inspection-quicktime-${q.label.toLowerCase().replace(/\s/g, '-')}`}
                      className={`py-2.5 rounded-xl border text-xs sm:text-sm font-medium transition-all ${
                        active ? 'bg-habita text-white border-habita' : 'border-gray-200 hover:border-habita'
                      }`}
                    >
                      <span className="block leading-tight">{q.label}</span>
                      <span className={`block text-[10px] mt-0.5 ${active ? 'text-white/80' : 'text-gray-400'}`}>
                        {formatFriendlyTime(q.value)}
                      </span>
                    </button>
                  );
                })}
              </div>
              <Label className="text-xs uppercase tracking-widest text-gray-500">O elige cualquier hora</Label>
              <Input
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                step={900}
                data-testid="inspection-time-input"
                className="mt-1.5 h-12 text-base"
              />
              {!timeNotInPast && (
                <p className="text-xs text-rose-600 mt-1.5">Esa hora ya pasó. Elige una hora futura.</p>
              )}
              <p className="text-[11px] text-gray-400 mt-2">Horario de Colombia (GMT-5)</p>
            </div>

            {dateIsValid && timeIsValid && timeNotInPast && (
              <div className="rounded-2xl bg-habita-cream p-4 text-sm text-habita-dark">
                <p className="font-medium">Visita programada para:</p>
                <p className="mt-1">
                  <strong>{formatFriendlyDate(date)}</strong> a las <strong>{formatFriendlyTime(time)}</strong>
                </p>
              </div>
            )}

            <Button
              disabled={!canContinue}
              onClick={() => setStep(2)}
              data-testid="inspection-continue-btn"
              className="btn-habita w-full h-12 rounded-full disabled:opacity-50"
            >
              Continuar <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        )}

        {step === 2 && (
          <form onSubmit={submit} className="p-5 sm:p-6 space-y-4" noValidate>
            <FormBanner msg={v.banner} testid="inspection-error-banner" />
            <div className="rounded-2xl bg-habita-cream p-4 text-sm">
              <p className="text-gray-600">Tu visita:</p>
              <p className="mt-1 font-medium text-habita-dark">
                {formatFriendlyDate(date)} · {formatFriendlyTime(time)}
              </p>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-500">Nombre completo</Label>
              <Input
                value={form.name}
                onChange={e => setField('name', e.target.value)}
                className={`mt-1.5 h-12 ${v.errorClass('name')}`}
                data-testid="inspection-name-input"
                {...v.field('name')}
              />
              <FormError msg={v.fieldErrors.name} testid="inspection-error-name" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-widest text-gray-500">Email</Label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={e => setField('email', e.target.value)}
                  className={`mt-1.5 h-12 ${v.errorClass('email')}`}
                  data-testid="inspection-email-input"
                  {...v.field('email')}
                />
                <FormError msg={v.fieldErrors.email} testid="inspection-error-email" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-gray-500">Teléfono</Label>
                <Input
                  type="tel"
                  value={form.phone}
                  onChange={e => setField('phone', e.target.value)}
                  className={`mt-1.5 h-12 ${v.errorClass('phone')}`}
                  data-testid="inspection-phone-input"
                  {...v.field('phone')}
                />
                <FormError msg={v.fieldErrors.phone} testid="inspection-error-phone" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-500">Notas (opcional)</Label>
              <textarea
                value={form.notes}
                onChange={e => setField('notes', e.target.value)}
                rows={3}
                className="mt-1.5 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-habita"
                placeholder="¿Algo que el agente deba saber?"
                data-testid="inspection-notes-input"
              />
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setStep(1)} className="flex-1 h-12 rounded-full">Atrás</Button>
              <Button
                type="submit"
                disabled={submitting}
                data-testid="inspection-submit-btn"
                className="btn-habita flex-1 h-12 rounded-full disabled:opacity-60"
              >
                {submitting ? 'Agendando...' : 'Confirmar visita'}
              </Button>
            </div>
          </form>
        )}

        {step === 3 && (
          <div className="p-8 sm:p-10 text-center">
            <div className="w-20 h-20 rounded-full bg-habita-mint mx-auto flex items-center justify-center mb-5">
              <Check className="w-10 h-10 text-habita" />
            </div>
            <h3 className="font-serif text-2xl font-bold text-habita-dark">¡Visita confirmada!</h3>
            <p className="text-gray-600 mt-3">Te enviaremos un email con los detalles a <strong>{form.email}</strong></p>
            <div className="mt-6 bg-habita-cream rounded-2xl p-5 text-left">
              <p className="text-sm"><strong>Fecha:</strong> {formatFriendlyDate(date)}</p>
              <p className="text-sm mt-1"><strong>Hora:</strong> {formatFriendlyTime(time)}</p>
              <p className="text-sm mt-1"><strong>Dirección:</strong> {property.address}, {property.city}</p>
            </div>
            <Button
              onClick={onClose}
              data-testid="inspection-done-btn"
              className="btn-habita mt-6 px-8 h-12 rounded-full"
            >
              Listo
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
